import { tableV2ColumnDefaults, tableV2DefaultColumnOrder } from './TableV2ColumnPolicy';
import { computed, createModel, signal } from '@preact/signals-react';
import type { ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { ConcreteDataType } from '@thinkalpha/language-services';
import { SortModel } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate, TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import type { TableClass, TableWidgetViewData } from '@thinkalpha/ui-types/contracts/workspace';
import { isEqual } from 'es-toolkit';
import { z } from 'zod';

export const tableV2ViewDataSchema = z.object({
    sort: SortModel.nullable(),
    columnWidths: z.record(z.string(), z.number()),
    columnOrder: z.array(z.string()).nullable(),
    columnVisibility: z.record(z.string(), z.boolean()),
    pinnedColumns: z.record(z.string(), z.union([z.boolean(), z.literal('left'), z.literal('right')])).nullable(),
});

export const tableV2GridStateSchema = z.object({
    viewData: tableV2ViewDataSchema.nullable().default(null),
});

export type TableV2GridState = z.output<typeof tableV2GridStateSchema>;

export interface TableV2GridModelShape {
    readonly columnTemplate: ReadonlySignal<ColumnTemplate>;
    readonly dirty: ReadonlySignal<boolean>;
    readonly resolvedColumns: ReadonlySignal<OutputColumnWithMetadata[]>;
    readonly symbolIdField: ReadonlySignal<string | undefined>;
    readonly viewData: ReadonlySignal<TableWidgetViewData | null>;
    toJSON(): TableV2GridState;
    updateColumnTemplate(columnTemplate: ColumnTemplate): void;
    markClean(): void;
}

export interface TableV2GridModelInit {
    columns: ReadonlySignal<OutputColumnWithMetadata[]>;
    persisted: TableV2GridState;
    tableClass: ReadonlySignal<TableClass | null>;
}

function orderedColumns(
    columns: readonly OutputColumnWithMetadata[],
    columnOrder: readonly string[] | null | undefined,
): OutputColumnWithMetadata[] {
    if (columnOrder === null || columnOrder === undefined) {
        return [...columns];
    }

    const columnsById = new Map<string, OutputColumnWithMetadata>(
        columns.map((column) => [column.id, column] as const),
    );
    const ordered: OutputColumnWithMetadata[] = [];
    for (const columnId of columnOrder) {
        const column = columnsById.get(columnId);
        if (column !== undefined) {
            ordered.push(column);
            columnsById.delete(columnId);
        }
    }
    for (const column of columns) {
        if (columnsById.has(column.id)) {
            ordered.push(column);
        }
    }
    return ordered;
}

function columnPin(value: boolean | 'left' | 'right' | undefined): 'left' | 'right' | undefined {
    return value === true ? 'left' : value === false ? undefined : value;
}

export function tableV2SymbolIdField(columns: readonly OutputColumnWithMetadata[]): string | undefined {
    return (
        columns.find((column) => column.matchSlug === Slugs.symbol)?.id ??
        columns.find((column) => column.unit === 'symbol')?.id ??
        columns.find((column) => column.id.trim().toLowerCase() === 'symbol')?.id
    );
}

function toTemplateColumn(
    column: OutputColumnWithMetadata,
    viewData: TableWidgetViewData | null,
    sortIndexById: ReadonlyMap<string, number>,
    tableClass: TableClass | null,
): TemplateColumn {
    const sort = viewData?.sort?.[column.id];
    const defaults = tableV2ColumnDefaults(column, tableClass);
    const shared = {
        id: column.id,
        name: defaults.name,
        dataType: column.dataType as ConcreteDataType,
        unit: typeof column.unit === 'string' ? column.unit : null,
        visible: viewData === null ? defaults.visible : (viewData.columnVisibility[column.id] ?? defaults.visible),
        width: viewData?.columnWidths[column.id],
        pinned: columnPin(viewData?.pinnedColumns?.[column.id]),
        sort: sort === 'ascending' ? ('asc' as const) : sort === 'descending' ? ('desc' as const) : undefined,
        sortIndex: sortIndexById.get(column.id),
        displaySettings: defaults.displaySettings,
        options: column.options,
    };

    if (column.type === 'formula') {
        return {
            ...shared,
            type: 'formula',
            imports: column.imports,
            formula: column.formula,
        };
    }

    return {
        ...shared,
        type: 'indicator',
        indicator: column.indicator,
    };
}

/** Projects raw legacy-table output metadata plus controlled widget layout into the ADR 0014 adapter input. */
export function tableV2ColumnTemplate(
    columns: readonly OutputColumnWithMetadata[],
    viewData: TableWidgetViewData | null,
    tableClass: TableClass | null = null,
): ColumnTemplate {
    const sortIndexById = new Map<string, number>(
        Object.keys(viewData?.sort ?? {}).map((columnId, index) => [columnId, index] as const),
    );

    return {
        name: 'Table v2',
        key: 'table',
        isTemplate: false,
        columns: orderedColumns(
            columns,
            viewData === null ? tableV2DefaultColumnOrder(tableClass) : viewData.columnOrder,
        ).map((column) => toTemplateColumn(column, viewData, sortIndexById, tableClass)),
        gridDisplaySettings: [],
        rowBasedDisplaySettings: [],
    };
}

/** Reduces the controlled template emitted by TableModelViewer back to Table widget persistence. */
export function tableV2ViewDataFromColumnTemplate(columnTemplate: ColumnTemplate): TableWidgetViewData {
    const sortedColumns = columnTemplate.columns
        .filter((column) => column.sort !== undefined)
        .toSorted((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0));

    return {
        sort: Object.fromEntries(
            sortedColumns.map((column) => [
                column.id,
                column.sort === 'asc' ? ('ascending' as const) : ('descending' as const),
            ]),
        ),
        columnWidths: Object.fromEntries(
            columnTemplate.columns.flatMap((column) =>
                column.width === undefined ? [] : ([[column.id, column.width]] as const),
            ),
        ),
        columnOrder: columnTemplate.columns.map((column) => column.id),
        columnVisibility: Object.fromEntries(columnTemplate.columns.map((column) => [column.id, column.visible])),
        pinnedColumns: Object.fromEntries(columnTemplate.columns.map((column) => [column.id, column.pinned ?? false])),
    };
}

export const TableV2GridModel: ModelConstructor<TableV2GridModelShape, [TableV2GridModelInit]> = createModel<
    TableV2GridModelShape,
    [TableV2GridModelInit]
>((init: TableV2GridModelInit) => {
    const viewData = signal<TableWidgetViewData | null>(init.persisted.viewData);
    const cleanViewData = signal<TableWidgetViewData | null>(init.persisted.viewData);
    const dirty = computed(() => !isEqual(viewData.value, cleanViewData.value));
    const columnTemplate = computed(() =>
        tableV2ColumnTemplate(init.columns.value, viewData.value, init.tableClass.value),
    );
    const symbolIdField = computed(() => tableV2SymbolIdField(init.columns.value));

    return {
        columnTemplate,
        dirty,
        resolvedColumns: init.columns,
        symbolIdField,
        viewData,
        toJSON(): TableV2GridState {
            return { viewData: viewData.value };
        },
        updateColumnTemplate(updatedColumnTemplate: ColumnTemplate): void {
            const updatedViewData = tableV2ViewDataFromColumnTemplate(updatedColumnTemplate);
            if (!isEqual(updatedViewData, viewData.value)) {
                viewData.value = updatedViewData;
            }
        },
        markClean(): void {
            cleanViewData.value = viewData.value;
        },
    };
});

export type TableV2GridModel = InstanceType<typeof TableV2GridModel>;
